import React, { useEffect, useState } from 'react';
import { X, ExternalLink, ShoppingBag, Layers, Star, Tag } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useLang } from '../../contexts/LanguageContext';
import { getLocalizedField } from '../../utils/localizedContent';


// Mapear api_provider → clave tcg_type del endpoint de productos
const PROVIDER_TO_TCG = {
  pokemon:    'pokemon',
  pokemontcg: 'pokemon',
  scryfall:   'magic',
  magic:      'magic',
  ygoprodeck: 'yugioh',
  yugioh:     'yugioh',
  optcg:      'onepiece',
  onepiece:   'onepiece',
  riftscribe: 'riftbound',
  riftbound:  'riftbound',
};

const RARITY_COLOR = {
  'Common':        'bg-white/10 text-white',
  'Uncommon':      'bg-emerald-500/20 text-emerald-300',
  'Rare':          'bg-blue-500/20 text-blue-300',
  'Rare Holo':     'bg-violet-500/20 text-violet-300',
  'Ultra Rare':    'bg-[#C9A962]/20 text-[#C9A962]',
  'Secret Rare':   'bg-pink-500/20 text-pink-300',
  'Mythic':        'bg-orange-500/20 text-orange-300',
  'Legendary':     'bg-yellow-500/20 text-yellow-300',
};

function rarityClass(rarity) {
  return RARITY_COLOR[rarity] || 'bg-white/10 text-[#A1A1AA]';
}

export default function CollectionCardModal({ card, lang, onClose }) {
  const [boosterData, setBoosterData] = useState(null);
  const [loadingBooster, setLoadingBooster] = useState(false);
  const { t } = useLang();

  useEffect(() => {
    if (!card) return;
    const tcgType = PROVIDER_TO_TCG[card.api_provider?.toLowerCase()] || card.api_provider;
    if (!tcgType || !card.set_name) return;
    setLoadingBooster(true);
    axios.get(`${API}/tcg/card/${tcgType}/products`, { params: { set_name: card.set_name } })
      .then(r => setBoosterData(r.data))
      .catch(() => setBoosterData(null))
      .finally(() => setLoadingBooster(false));
  }, [card]);

  if (!card) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 px-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0F1F1A] border border-[#C9A962]/30 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Close */}
        <div className="flex justify-end p-4 pb-0">
          <button onClick={onClose} className="text-[#A1A1AA] hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Card content */}
        <div className="flex gap-5 px-5 pb-4">
          {/* Image */}
          <div className="flex-shrink-0 w-32">
            <div className="aspect-[2/3] rounded-xl overflow-hidden bg-[#162B24] shadow-lg">
              <img
                src={card.image_url || 'https://via.placeholder.com/150x210'}
                alt={card.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0 pt-1">
            <h2 className="text-[#F5F0E6] font-bold text-lg leading-tight mb-3" style={{ fontFamily: 'Cinzel, serif' }}>
              {card.name}
            </h2>

            <div className="flex flex-wrap gap-2 mb-3">
              {card.set_name && (
                <span className="flex items-center gap-1 px-2 py-1 bg-emerald-500/15 text-emerald-300 rounded-full text-xs">
                  <Layers size={10} /> {card.set_name}
                </span>
              )}
              {card.rarity && (
                <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${rarityClass(card.rarity)}`}>
                  <Star size={10} /> {card.rarity}
                </span>
              )}
            </div>

            {card.type_line && (
              <p className="text-[#A1A1AA] text-xs mb-2 flex items-center gap-1">
                <Tag size={10} /> {card.type_line}
              </p>
            )}

            {card.quantity > 1 && (
              <p className="text-[#C9A962] text-sm font-semibold mt-2">
                {t('collectionsShared.in_collection')}: ×{card.quantity}
              </p>
            )}

            {card.api_provider && (
              <p className="text-[#52525B] text-xs mt-2 uppercase tracking-wide">
                {PROVIDER_TO_TCG[card.api_provider?.toLowerCase()] || card.api_provider}
              </p>
            )}
          </div>
        </div>

        {/* Booster / Products section */}
        <div className="border-t border-white/5 px-5 py-4">
          <h3 className="text-[#A1A1AA] text-xs font-semibold uppercase tracking-wider mb-3">
            {t('collectionsShared.available_in_store')}
          </h3>

          {loadingBooster ? (
            <div className="flex items-center gap-2 text-[#A1A1AA] text-sm">
              <div className="w-4 h-4 border-2 border-[#C9A962]/30 border-t-[#C9A962] rounded-full animate-spin" />
              {t('collectionsShared.searching')}
            </div>
          ) : boosterData?.booster ? (
            <div className="space-y-3">
              {/* Booster info */}
              <div className="flex items-center gap-3 p-3 bg-[#162B24] rounded-xl">
                {boosterData.booster.logo_url ? (
                  <img src={boosterData.booster.logo_url} alt="" className="w-10 h-10 object-contain" />
                ) : (
                  <div className="w-10 h-10 bg-[#C9A962]/20 rounded-lg flex items-center justify-center">
                    <Layers size={18} className="text-[#C9A962]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{boosterData.booster.name}</p>
                  {boosterData.booster.set_code && (
                    <p className="text-[#A1A1AA] text-xs">{boosterData.booster.set_code}</p>
                  )}
                </div>
                <a
                  href={`/boosters/${boosterData.booster.booster_id}`}
                  className="text-[#C9A962] text-xs hover:underline flex items-center gap-1"
                >
                  <ExternalLink size={12} />{t('catalog.view_set')}
                </a>
              </div>

              {/* Linked products */}
              {boosterData.products?.length > 0 && (
                <div className="space-y-2">
                  {boosterData.products.map(product => (
                    <a
                      key={product.product_id}
                      href={`/store/${product.product_id}`}
                      className="flex items-center gap-3 p-2.5 bg-[#0A1612] border border-white/5 rounded-xl hover:border-[#C9A962]/30 transition-colors"
                    >
                      {product.images?.[0] ? (
                        <img src={product.images[0]} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center">
                          <ShoppingBag size={16} className="text-[#A1A1AA]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{getLocalizedField(product, 'name', lang, product.name)}</p>
                        <p className="text-[#C9A962] text-xs">{product.price?.toFixed(2)}€</p>
                      </div>
                      <ExternalLink size={13} className="text-[#A1A1AA] flex-shrink-0" />
                    </a>
                  ))}
                </div>
              )}

              {boosterData.products?.length === 0 && (
                <p className="text-[#52525B] text-xs">
                  {t('collectionsShared.set_without_products')}
                </p>
              )}
            </div>
          ) : (
            <p className="text-[#52525B] text-sm">
              {t('collectionsShared.expansion_not_available')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
